import * as duckdb from '@duckdb/duckdb-wasm';
import { createProxyFetcher } from '@rdrive/data-transport';
import { DUCKDB_WASM_VERSION } from './version.js';

/**
 * Per-call options for the four download loaders. Every field is opt-in: omit the
 * argument and a loader behaves exactly as it did before these options existed.
 *
 * @typedef {Object} LoaderOptions
 * @property {(progress: Object) => void} [progressCallback] called with
 *   `{ tableName, status: 'completed', timeMs }` when the table is loaded; the paged loader
 *   also sends `{ tableName, status: 'loading', currentPage, rowsLoaded }` after each page
 *   from page 2 on, and adds `totalPages`/`rowsLoaded` to its `'completed'` call
 * @property {boolean} [throwOnError=false] rethrow a failed fetch/load instead of logging it
 *   and returning `false` (the not-initialised guard still returns `false`)
 * @property {Object<string, string>} [headers] extra request headers, merged OVER the loader's
 *   own: the two parquet loaders send `Accept: application/vnd.apache.parquet`, the Arrow and
 *   JSON loaders send none. Omitted, the request is exactly what it was before this option
 *   existed (issue-analysis's JSON tables pass `{ Accept: 'application/json' }`, which its own
 *   copy of this service always sent)
 */

// A non-2xx proxy response as an Error with the same shape ApiClient throws, so a caller
// using `throwOnError` can tell an auth failure from an outage. The message is unchanged.
const httpError = (response) => {
  const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
  error.status = response.status;
  error.statusText = response.statusText;
  error.response = response;
  return error;
};

// A loader's GET init: its own default headers (or none) with the caller's `headers` merged over
// them. Without `headers` it is the very init the loader sent before the option existed —
// `{ method: 'GET' }` for Arrow/JSON — so an app that passes none sees no request change.
const getInit = (defaults, headers) => {
  if (!defaults && !headers) return { method: 'GET' };
  return { method: 'GET', headers: { ...defaults, ...headers } };
};
const PARQUET = { 'Accept': 'application/vnd.apache.parquet' };

export class DuckDBService {
  constructor() {
    this.isInitialized = false;
    this.isDataLoaded = false;
    this.db = null;
    this.rawConn = null;
    this.logger = new duckdb.ConsoleLogger();
    this.config = null;
    // Owns the proxy URL/auth scheme for all four loaders — see ../transport/proxyFetcher.js
    this.fetcher = null;

    this.emptyResult = {
      toArray: () => [],
      toString: () => "",
      getChild: () => null,
      schema: [],
      numCols: 0,
      numRows: 0,
    };
    
    this.conn = {
      query: async (queryString) => {
        try {
          if (!this.isInitialized || !this.isDataLoaded || !this.rawConn) {
            return this.emptyResult;
          }
          return await this.rawConn.query(queryString);
        } catch (error) {
          console.error(`Query failed: ${error.message}`);
          return this.emptyResult;
        }
      },
      // Bypasses the isDataLoaded gate (usable while tables are still loading) and does not
      // swallow errors or poison isDataLoaded — a failed query throws to the caller.
      queryRaw: async (queryString) => {
        if (!this.isInitialized || !this.rawConn) {
          return this.emptyResult;
        }
        return await this.rawConn.query(queryString);
      },
      loadData: async (queryString) => {
        if (!this.isInitialized) {
          console.error('Cannot load data: DuckDB not initialized');
          return false;
        }
    
        try {
          await this.rawConn.query(queryString);
          return true;
        } catch (error) {
          console.error('Error loading data:', error);
          this.isDataLoaded = false;
          return false;
        }
      }
    };
  }

  /**
   * @param {object} options
   * @param {string} [options.proxyUrl]
   * @param {string} [options.apiUrl]
   * @param {string} [options.scopedToken]
   * @param {import('@rdrive/data-transport/src/proxyFetcher.js').ProxyFetcher} [options.fetcher] pass a
   *   fetcher built from a config GETTER to make the token refreshable later
   * @param {string} [options.wasmVersion] DuckDB WASM version for the CDN URLs; defaults to
   *   the build-time `DUCKDB_WASM_VERSION` (the pnpm catalog pin) — see ./version.js
   */
  async initialize({ proxyUrl, apiUrl, scopedToken, fetcher, wasmVersion } = {}) {
    try {
      // Store config for later use. Either form is accepted: a raw config
      // (wrapped in a fetcher here) or a fetcher built by the caller.
      if (fetcher) {
        this.fetcher = fetcher;
        this.config = fetcher.config();
      } else {
        this.config = { proxyUrl, apiUrl, scopedToken };
        this.fetcher = createProxyFetcher(this.config);
      }

      // Use CDN URLs directly - these are served with proper MIME types. The version is the
      // pnpm catalog pin, injected at build time by @rdrive/vite-config's createDashboardVite()
      // (see ./version.js) — deliberately NO literal fallback, so the catalog cannot drift
      // out of sync with what the browser loads.
      const version = wasmVersion ?? DUCKDB_WASM_VERSION;
      if (!version) {
        throw new Error(
          "@rdrive/data-duckdb: DuckDB CDN version unknown — build with @rdrive/vite-config's " +
            'createDashboardVite(), or pass initialize({ wasmVersion }).',
        );
      }
      const wasmUrl = `https://cdn.jsdelivr.net/npm/@duckdb/duckdb-wasm@${version}/dist/duckdb-eh.wasm`;
      const workerUrl = `https://cdn.jsdelivr.net/npm/@duckdb/duckdb-wasm@${version}/dist/duckdb-browser-eh.worker.js`;

      // Fetch worker script and create blob URL to avoid CORS issues
      const workerResponse = await fetch(workerUrl);
      const workerCode = await workerResponse.text();
      const workerBlob = new Blob([workerCode], { type: 'application/javascript' });
      const workerBlobUrl = URL.createObjectURL(workerBlob);

      const MANUAL_BUNDLES = {
        eh: {
          mainModule: wasmUrl,
          mainWorker: workerBlobUrl,
        }
      };

      const bundle = await duckdb.selectBundle(MANUAL_BUNDLES);
      const worker = new Worker(bundle.mainWorker);

      //const duckDBConfig = {
      //  maximumThreads: 4, // Adjust the number based on your needs
      //};

      this.db = new duckdb.AsyncDuckDB(this.logger, worker/*, duckDBConfig*/);

      console.log('Instantiating DuckDB...');
      await this.db.instantiate(bundle.mainModule);
      console.log('DuckDB instantiated successfully');

      console.log('Creating DuckDB connection...');
      this.rawConn = await this.db.connect();
      console.log('DuckDB connection created successfully');

      this.isInitialized = true;
      return true;
    } catch (error) {
      console.error('Error initializing DuckDB:', error);
      this.isInitialized = false;
      return false;
    }
  }

  /** @param {string} path @param {string} tableName @param {LoaderOptions} [options] */
  async downloadAndInsertParquet(path, tableName, { progressCallback = null, throwOnError = false, headers } = {}) {
    if (!this.isInitialized || !this.fetcher) {
      console.warn('Cannot download parquet: DuckDB not initialized or config missing');
      return false;
    }

    try {
      const startTime = performance.now();
      this.logger.log(`Fetching ${tableName} from ${this.fetcher.url(path)} at ${startTime}`);
      const response = await this.fetcher(path, getInit(PARQUET, headers));
      if (!response.ok) throw httpError(response);
      const blob = await response.blob();
      const arrayBuffer = await blob.arrayBuffer();
      await this.db.registerFileBuffer(`${tableName}.parquet`, new Uint8Array(arrayBuffer));
      await this.conn.loadData(`CREATE TABLE IF NOT EXISTS ${tableName} AS SELECT * FROM parquet_scan('${tableName}.parquet')`);
      const totalTime = performance.now() - startTime;
      this.logger.log(`Table ${tableName} created successfully in ${totalTime}ms`);
      if (progressCallback) progressCallback({ tableName, status: 'completed', timeMs: totalTime });
      return true;
    } catch (error) {
      this.logger.log(`Error creating table ${tableName} from ${path}: ${error.message}`);
      if (throwOnError) throw error;
      return false;
    }
  }

  /** @param {string} path @param {string} tableName @param {LoaderOptions} [options] */
  async downloadAndInsertParquetWithPaging(path, tableName, { progressCallback = null, throwOnError = false, headers } = {}) {
    if (!this.isInitialized || !this.fetcher) {
      console.warn('Cannot download parquet: DuckDB not initialized or config missing');
      return false;
    }
  
    try {
      const startTime = performance.now();
      this.logger.log(`Starting paged fetch for ${tableName} at ${startTime}`);
      
      // First create the table structure. `page` goes through the fetcher's
      // params so it still lands AFTER url/scopedToken, as it always has.
      let pageNum = 1;
      
      this.logger.log(`Fetching first page from ${this.fetcher.url(path, { page: pageNum })}`);
      const firstResponse = await this.fetcher(path, getInit(PARQUET, headers), { page: pageNum });
      
      if (!firstResponse.ok) throw httpError(firstResponse);
      
      const firstBlob = await firstResponse.blob();
      if (firstBlob.size === 0) {
        this.logger.log(`No data received for ${tableName}`);
        return false;
      }
      
      const firstArrayBuffer = await firstBlob.arrayBuffer();
      await this.db.registerFileBuffer(`${tableName}_page_${pageNum}.parquet`, new Uint8Array(firstArrayBuffer));
      
      // Create the table with the first page
      await this.conn.loadData(`CREATE TABLE IF NOT EXISTS ${tableName} AS SELECT * FROM parquet_scan('${tableName}_page_${pageNum}.parquet')`);
      this.logger.log(`Table ${tableName} created with page ${pageNum}`);
      
      // Continue loading additional pages. Endpoints signal "no more data" in one of two
      // ways: an empty body (blob.size === 0), or a valid but row-less parquet past the last
      // page — its body still carries the PAR1 magic + footer, so blob.size is never 0.
      // Stop on whichever comes first. (Upstreamed from issue-analysis, whose
      // status-history-paging endpoint does the latter.)
      let totalRowsInserted = await this.getRowCount(tableName);
      let pagesInserted = 1;
      // A row-less first page means there is nothing more to fetch.
      let hasMoreData = totalRowsInserted > 0;
      
      while (hasMoreData) {
        pageNum++;
        
        this.logger.log(`Fetching page ${pageNum} from ${this.fetcher.url(path, { page: pageNum })}`);
        const response = await this.fetcher(path, getInit(PARQUET, headers), { page: pageNum });
        
        if (!response.ok) throw httpError(response);
        
        const blob = await response.blob();
        if (blob.size === 0) {
          this.logger.log(`No more data available after page ${pageNum-1} (empty response)`);
          hasMoreData = false;
          break;
        }
        
        const arrayBuffer = await blob.arrayBuffer();
        const pageFile = `${tableName}_page_${pageNum}.parquet`;
        await this.db.registerFileBuffer(pageFile, new Uint8Array(arrayBuffer));

        // A non-empty body may still be a row-less parquet (end of data): check before inserting
        const pageRowCount = await this.getParquetRowCount(pageFile);
        if (pageRowCount === 0) {
          this.logger.log(`No more data available after page ${pageNum-1} (empty parquet)`);
          await this.db.dropFile(pageFile);
          hasMoreData = false;
          break;
        }
        
        // Append data to the existing table
        await this.conn.loadData(`INSERT INTO ${tableName} SELECT * FROM parquet_scan('${pageFile}')`);
        totalRowsInserted += pageRowCount;
        pagesInserted++;
        
        this.logger.log(`Inserted ${pageRowCount} rows from page ${pageNum}`);
        if (progressCallback) {
          progressCallback({ tableName, currentPage: pageNum, rowsLoaded: totalRowsInserted, status: 'loading' });
        }
        
        // Free up memory by dropping the page's file buffer after insertion. `dropFile` is
        // duckdb-wasm's API for this — there is no `unregisterFileBuffer`; calling that name
        // threw a TypeError here as soon as a second page was inserted.
        await this.db.dropFile(pageFile);
      }
      
      // Drop the first page's file buffer too
      await this.db.dropFile(`${tableName}_page_1.parquet`);
      
      const totalTime = performance.now() - startTime;
      this.logger.log(`Table ${tableName} created successfully with ${totalRowsInserted} total rows in ${totalTime}ms`);
      if (progressCallback) {
        progressCallback({ tableName, totalPages: pagesInserted, rowsLoaded: totalRowsInserted, status: 'completed', timeMs: totalTime });
      }
      return true;
    } catch (error) {
      this.logger.log(`Error creating paged table ${tableName} from ${path}: ${error.message}`);
      if (throwOnError) throw error;
      return false;
    }
  }
  
  // Row count of a table, as a Number. Goes through queryRaw, NOT the gated conn.query:
  // the paged loader calls this while isDataLoaded is still false, when conn.query answers
  // with an empty result. Throws if the table does not exist.
  async getRowCount(tableName) {
    const result = await this.conn.queryRaw(`SELECT COUNT(*) as count FROM ${tableName}`);
    const rows = result.toArray();
    return rows.length > 0 ? Number(rows[0].count) : 0;
  }

  // Row count of a REGISTERED parquet buffer (read from its footer), without inserting it.
  // Ungated for the same reason as getRowCount.
  async getParquetRowCount(fileName) {
    const result = await this.conn.queryRaw(`SELECT COUNT(*) as count FROM parquet_scan('${fileName}')`);
    const rows = result.toArray();
    return rows.length > 0 ? Number(rows[0].count) : 0;
  }

  // Run SQL on the raw connection: works before isDataLoaded is set, and throws on error
  // instead of returning an empty result. `conn.query` keeps its gate and its swallowing.
  async queryRaw(sql) {
    return this.conn.queryRaw(sql);
  }

  // Whether a table (or view) with this name exists in the current catalog.
  // Schema-agnostic: lets apps decide lazily whether to fetch (e.g. a large
  // parquet only needed by one page) without re-downloading on revisit.
  async tableExists(tableName) {
    try {
      const result = await this.conn.query(`
        SELECT table_name
        FROM information_schema.tables
        WHERE table_name = '${String(tableName).replace(/'/g, "''")}'
      `);
      return result.toArray().length > 0;
    } catch (error) {
      this.logger.log(`Error checking whether table ${tableName} exists: ${error.message}`);
      return false;
    }
  }

  // Build (or rebuild) a DERIVED table from a SELECT. The SQL is the app's —
  // this service never knows the schema; it only owns the create/replace
  // mechanics, mirroring the download loaders above for computed tables.
  async createTableFromQuery(tableName, selectSql, { replace = true } = {}) {
    if (!this.isInitialized) {
      console.warn(`Cannot create table ${tableName}: DuckDB not initialized`);
      return false;
    }
    try {
      const startTime = performance.now();
      const drop = replace ? `DROP TABLE IF EXISTS ${tableName};` : '';
      const create = replace ? `CREATE TABLE ${tableName} AS` : `CREATE TABLE IF NOT EXISTS ${tableName} AS`;
      await this.conn.loadData(`
        ${drop}
        ${create}
        ${selectSql}
      `);
      this.logger.log(`Derived table ${tableName} ${replace ? 'rebuilt' : 'created'} in ${performance.now() - startTime}ms`);
      return true;
    } catch (error) {
      this.logger.log(`Error creating derived table ${tableName}: ${error.message}`);
      return false;
    }
  }

  /** @param {string} path @param {string} tableName @param {LoaderOptions} [options] */
  async downloadAndInsertArrow(path, tableName, { progressCallback = null, throwOnError = false, headers } = {}) {
    if (!this.isInitialized || !this.fetcher) {
      console.warn('Cannot download Arrow file: DuckDB not initialized or config missing');
      return false;
    }
  
    try {
      const startTime = performance.now();
      this.logger.log(`Fetching ${tableName} from ${this.fetcher.url(path)} at ${startTime}`);
  
      // Stream the response if the browser supports it
      const response = await this.fetcher(path, getInit(null, headers));
      if (!response.ok) throw httpError(response);
      
      // Define EOS signal according to the Arrow IPC streaming format
      const EOS = new Uint8Array([255, 255, 255, 255, 0, 0, 0, 0]);
      
      if (response.body && response.body.getReader) {
        // Stream processing for better memory efficiency
        const reader = response.body.getReader();
        const inserts = [];
        
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          inserts.push(this.rawConn.insertArrowFromIPCStream(value, { name: tableName }));
        }
        
        // Write the End-of-Stream marker
        inserts.push(this.rawConn.insertArrowFromIPCStream(EOS, { name: tableName }));
        await Promise.all(inserts);
      } else {
        // Fallback for browsers that don't support streaming
        const arrayBuffer = await response.arrayBuffer();
        await this.rawConn.insertArrowFromIPCStream(new Uint8Array(arrayBuffer), { name: tableName });
        await this.rawConn.insertArrowFromIPCStream(EOS, { name: tableName });
      }
      
      const totalTime = performance.now() - startTime;
      this.logger.log(`Table ${tableName} created successfully in ${totalTime}ms`);
      if (progressCallback) progressCallback({ tableName, status: 'completed', timeMs: totalTime });
      return true;
    } catch (error) {
      this.logger.log(`Error creating table ${tableName} from ${path}: ${error.message}`);
      if (throwOnError) throw error;
      return false;
    }
  }

  /**
   * @param {string} path
   * @param {string} tableName
   * @param {LoaderOptions & { append?: boolean }} [options] `append: true` creates the table
   *   EMPTY (schema inferred from this payload) if it does not exist and then INSERTs the rows,
   *   so a repeat load adds to the table — and a failed INSERT is an error. The default is
   *   `CREATE TABLE IF NOT EXISTS … AS SELECT`: the first load creates the table and a repeat
   *   load leaves it untouched.
   */
  async downloadAndInsertJSON(path, tableName, { progressCallback = null, throwOnError = false, append = false, headers } = {}) {
    if (!this.isInitialized || !this.fetcher) {
      console.warn('Cannot download JSON: DuckDB not initialized or config missing');
      return false;
    }

    try {
      const startTime = performance.now();
      this.logger.log(`Fetching ${tableName} from ${this.fetcher.url(path)} at ${startTime}`);
      const response = await this.fetcher(path, getInit(null, headers));
      if (!response.ok) throw httpError(response);
      
      const jsonData = await response.json();
      const dataArray = Array.isArray(jsonData) ? jsonData : [jsonData];
      
      if (dataArray.length === 0) {
        throw new Error('No data received');
      }

      const jsonString = JSON.stringify(dataArray);
      const encoder = new TextEncoder();
      const jsonBuffer = encoder.encode(jsonString);
      
      await this.db.registerFileBuffer(`${tableName}.json`, jsonBuffer);
      
      if (append) {
        await this.conn.loadData(`
        CREATE TABLE IF NOT EXISTS ${tableName} AS
        SELECT * FROM read_json_auto('${tableName}.json') WHERE false
      `);
        const inserted = await this.conn.loadData(`
        INSERT INTO ${tableName}
        SELECT * FROM read_json_auto('${tableName}.json')
      `);
        if (!inserted) throw new Error(`Insert into ${tableName} failed`);
      } else {
        await this.conn.loadData(`
        CREATE TABLE IF NOT EXISTS ${tableName} AS 
        SELECT * FROM read_json_auto('${tableName}.json')
      `);
      }
      
      const totalTime = performance.now() - startTime;
      this.logger.log(`Table ${tableName} ${append ? 'appended to' : 'created'} successfully in ${totalTime}ms`);
      if (progressCallback) progressCallback({ tableName, status: 'completed', timeMs: totalTime });
      return true;
    } catch (error) {
      this.logger.log(`Error creating table ${tableName} from ${path}: ${error.message}`);
      if (throwOnError) throw error;
      return false;
    }
  }

  async logQuery(query) {
    try {
      const result = await this.conn.query(query);
      
      const data = [];
      for (let row of result) {
        data.push({...row});
      }

      console.table(data);
      return data;
    } catch (error) {
      this.logger.log(`Error executing query "${query}": ${error.message}`);
      return [];
    }
  }

  async showSchema(tableName) {
    try {
      const result = await this.conn.query(`
        SELECT 
          column_name,
          data_type,
          is_nullable
        FROM information_schema.columns 
        WHERE table_name = '${tableName}'
      `);
      
      const data = result.toArray();
      
      console.log("Data to display:", data); // Check the actual data
      console.table(data);
      
      return data;
    } catch (error) {
      console.error(`Error getting schema for table ${tableName}:`, error);
      return [];
    }
  }
}